import { ChevronRight, ContentCopy, ExpandMore, LockOutlined, Search } from '@mui/icons-material';
import {
    Box,
    Chip,
    IconButton,
    InputAdornment,
    ListItemButton,
    ListItemText,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { useState } from 'react';

import {
    getCategoryLabelsForType,
    getMandatoryPlaceholdersForType,
    getPlaceholdersByCategoryForType,
} from 'config/placeholders';
import { useToast } from 'hooks/use-toast';
import type { TemplateType } from 'types';

interface VariablesPanelProps {
    onInsert: (token: string, mandatory: boolean) => void;
    templateType?: TemplateType;
}

export function VariablesPanel({
    onInsert,
    templateType = 'visa-application',
}: VariablesPanelProps) {
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
        new Set(['client', 'program'])
    );
    const { toast } = useToast();
    const mandatoryPlaceholders = getMandatoryPlaceholdersForType(templateType);
    const placeholdersByCategory = getPlaceholdersByCategoryForType(templateType);
    const categoryLabels = getCategoryLabelsForType(templateType);

    const toggleCategory = (category: string) => {
        const newExpanded = new Set(expandedCategories);
        if (newExpanded.has(category)) {
            newExpanded.delete(category);
        } else {
            newExpanded.add(category);
        }
        setExpandedCategories(newExpanded);
    };

    const copyToClipboard = (token: string) => {
        navigator.clipboard.writeText(token);
        toast({
            title: 'Copied',
            description: `${token} copied to clipboard`,
        });
    };

    const filteredPlaceholders = Object.entries(placeholdersByCategory).reduce(
        (acc, [category, placeholders]) => {
            const filtered = placeholders
                .filter((p): p is NonNullable<typeof p> => Boolean(p))
                .filter(
                    (p) =>
                        p.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        p.token.toLowerCase().includes(searchQuery.toLowerCase())
                );
            if (filtered.length > 0) {
                acc[category] = filtered;
            }
            return acc;
        },
        {} as Record<string, (typeof placeholdersByCategory)[string]>
    );

    const renderActions = (token: string, mandatory: boolean) => (
        <Stack
            className="variable-actions"
            direction="row"
            spacing={0.5}
            sx={{ opacity: 0, transition: 'opacity 0.15s' }}
        >
            <Tooltip title="Insert">
                <IconButton
                    size="small"
                    aria-label="Insert"
                    onClick={() => onInsert(token, mandatory)}
                >
                    <ChevronRight sx={{ fontSize: 16 }} />
                </IconButton>
            </Tooltip>
            <Tooltip title="Copy">
                <IconButton size="small" aria-label="Copy" onClick={() => copyToClipboard(token)}>
                    <ContentCopy sx={{ fontSize: 14 }} />
                </IconButton>
            </Tooltip>
        </Stack>
    );

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                    Variables
                </Typography>
                <TextField
                    size="small"
                    fullWidth
                    placeholder="Search variables..."
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search sx={{ fontSize: 18, color: 'text.secondary' }} />
                                </InputAdornment>
                            ),
                        },
                    }}
                />
            </Box>

            <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                {mandatoryPlaceholders.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
                            <LockOutlined sx={{ fontSize: 16, color: 'primary.main' }} />
                            <Typography
                                variant="caption"
                                sx={{ fontWeight: 700, color: 'primary.main', letterSpacing: 0.4 }}
                            >
                                MANDATORY FIELDS
                            </Typography>
                        </Stack>
                        <Stack spacing={0.5}>
                            {mandatoryPlaceholders
                                .filter(
                                    (placeholder): placeholder is NonNullable<typeof placeholder> =>
                                        Boolean(placeholder) &&
                                        (!searchQuery ||
                                            placeholder.label
                                                .toLowerCase()
                                                .includes(searchQuery.toLowerCase()) ||
                                            placeholder.token
                                                .toLowerCase()
                                                .includes(searchQuery.toLowerCase()))
                                )
                                .map((placeholder) => (
                                    <Box
                                        key={placeholder.token}
                                        sx={{
                                            p: 1,
                                            borderRadius: 1,
                                            border: 1,
                                            borderColor: 'primary.light',
                                            '&:hover': { bgcolor: 'action.hover' },
                                            '&:hover .variable-actions': { opacity: 1 },
                                        }}
                                    >
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            sx={{ alignItems: 'flex-start' }}
                                        >
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ fontWeight: 500 }}
                                                >
                                                    {placeholder.label}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    noWrap
                                                    sx={{
                                                        display: 'block',
                                                        fontFamily: 'monospace',
                                                    }}
                                                >
                                                    {placeholder.token}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    Sample: {placeholder.sampleValue}
                                                </Typography>
                                            </Box>
                                            {renderActions(placeholder.token, true)}
                                        </Stack>
                                    </Box>
                                ))}
                        </Stack>
                    </Box>
                )}

                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 700, letterSpacing: 0.4, display: 'block', mb: 1 }}
                >
                    OPTIONAL FIELDS
                </Typography>
                <Stack spacing={1}>
                    {Object.entries(filteredPlaceholders).map(([category, placeholders]) => {
                        const optionalPlaceholders = placeholders.filter(
                            (placeholder) => !placeholder.mandatory
                        );
                        if (optionalPlaceholders.length === 0) return null;

                        const isExpanded = expandedCategories.has(category);

                        return (
                            <Box key={category}>
                                <ListItemButton
                                    onClick={() => toggleCategory(category)}
                                    sx={{ borderRadius: 1, px: 1 }}
                                >
                                    <ListItemText
                                        primary={categoryLabels[category]}
                                        slotProps={{
                                            primary: { sx: { fontSize: 14, fontWeight: 500 } },
                                        }}
                                    />
                                    <Chip
                                        size="small"
                                        color="primary"
                                        label={optionalPlaceholders.length}
                                        sx={{ mr: 1, height: 22, fontWeight: 600 }}
                                    />
                                    {isExpanded ? (
                                        <ExpandMore sx={{ fontSize: 18 }} />
                                    ) : (
                                        <ChevronRight sx={{ fontSize: 18 }} />
                                    )}
                                </ListItemButton>

                                {isExpanded && (
                                    <Stack spacing={0.5} sx={{ ml: 1, mt: 0.5 }}>
                                        {optionalPlaceholders.map((placeholder) => (
                                            <Box
                                                key={placeholder.token}
                                                sx={{
                                                    p: 1,
                                                    borderRadius: 1,
                                                    '&:hover': { bgcolor: 'action.hover' },
                                                    '&:hover .variable-actions': { opacity: 1 },
                                                }}
                                            >
                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    sx={{ alignItems: 'flex-start' }}
                                                >
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{ fontWeight: 500 }}
                                                        >
                                                            {placeholder.label}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            noWrap
                                                            sx={{
                                                                display: 'block',
                                                                fontFamily: 'monospace',
                                                            }}
                                                        >
                                                            {placeholder.token}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                        >
                                                            Sample: {placeholder.sampleValue}
                                                        </Typography>
                                                        {placeholder.description ? (
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                sx={{
                                                                    display: 'block',
                                                                    fontStyle: 'italic',
                                                                }}
                                                            >
                                                                {placeholder.description}
                                                            </Typography>
                                                        ) : null}
                                                    </Box>
                                                    {renderActions(placeholder.token, false)}
                                                </Stack>
                                            </Box>
                                        ))}
                                    </Stack>
                                )}
                            </Box>
                        );
                    })}
                </Stack>
            </Box>
        </Box>
    );
}
